import { Select, SelectChangeEvent } from '@mui/material';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useEquipmentDetailsQuery } from 'api/queries/useEquipmentDetailsQuery';
import {
  DocumentAttachment,
  GoBackButtonWithTitle,
  ImageGallery,
  ListItemImageAttachment,
  Spinner,
  VideoAttachment,
  VideoGallery,
} from 'components';
import { DETAILS_FILE_NAME_LENGTH_LIMIT } from 'constants/index';
import { useModal, useWindowSize } from 'hooks';
import theme from 'styles/theme';
import { AttachmentType } from 'types';
import { hideDocNameMidPart } from 'utils';

import {
  AttachmentsSectionW,
  AttachmentsW,
  ContentW,
  DocsGridW,
  FilterMenuItem,
  Header,
  MediaColumnW,
  MediaSectionW,
  PageTitle,
  PageW,
  SectionLabel,
  SectionTitle,
  SectionW,
} from './styled';

const attachmentStyles = { width: '74px', height: '74px', marginRight: '0px' };
const attachmentMobileStyles = { width: '87px', height: '87px', marginRight: '0px' };

type FilterValue = AttachmentType | 'all';

export function EquipmentAttachmentsPage(): ReactElement {
  const { employeeId = '', equipmentId = '' } = useParams<string>();
  const { t } = useTranslation();
  const { data, isLoading } = useEquipmentDetailsQuery({ userId: employeeId, equipmentId });
  const [filter, setFilter] = useState<FilterValue>('all');
  const { isMobile } = useWindowSize();
  const navigate = useNavigate();

  const {
    isOpen: isOpenImageGallery,
    handleOpenModal: handleOpenImageGallery,
    handleCloseModal: handleCloseImageGallery,
  } = useModal();

  const {
    isOpen: isOpenVideoGallery,
    handleOpenModal: handleOpenVideoGallery,
    handleCloseModal: handleCloseVideoGallery,
  } = useModal();

  const handleFilterChange = (e: SelectChangeEvent<unknown>) => {
    setFilter(e.target.value as FilterValue);
  };

  const allAttachments = data?.attachments ?? [];
  const filteredAttachments =
    filter === 'all' ? allAttachments : allAttachments.filter((a) => a.type === filter);

  const imagesList = filteredAttachments.filter((a) => a.type === 'image');
  const videosList = filteredAttachments.filter((a) => a.type === 'video');
  const docsList = filteredAttachments.filter((a) => a.type === 'document');

  const equipmentName = data?.name ?? '';
  const title = `${t('equipments.title')} / ${equipmentName} / ${t(
    'equipments.attachments.title',
  )}`;
  const attachStyles = isMobile ? attachmentMobileStyles : attachmentStyles;

  const filterOptions: { value: FilterValue; label: string }[] = [
    { value: 'all', label: t('equipments.attachments.filter.all') },
    { value: 'image', label: t('equipments.attachments.filter.images') },
    { value: 'video', label: t('equipments.attachments.filter.videos') },
    { value: 'document', label: t('equipments.attachments.filter.documents') },
  ];

  return (
    <PageW>
      <ContentW>
        <GoBackButtonWithTitle
          title={t('common.back')}
          onClick={() => navigate(-1)}
          containerSx={{
            marginTop: 0,
            maxWidth: '100%',
          }}
        />
        <Header>
          <PageTitle>{title}</PageTitle>

          <Select
            value={filter}
            onChange={handleFilterChange}
            variant="outlined"
            size="small"
            sx={{
              height: '56px',
              width: '168px',
              borderRadius: '8px',
              fontSize: 14,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.grey,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1px',
                borderColor: theme.palette.primary.grey,
              },
            }}
          >
            {filterOptions.map(({ value, label }) => (
              <FilterMenuItem key={value} value={value}>
                {label}
              </FilterMenuItem>
            ))}
          </Select>
        </Header>

        {isLoading && <Spinner />}

        {!isLoading && (
          <AttachmentsSectionW>
            <SectionTitle>{t('equipments.attachments.available')}</SectionTitle>

            {docsList.length > 0 && (
              <SectionW>
                <SectionLabel>{t('equipments.attachments.filter.documents')}</SectionLabel>
                <DocsGridW>
                  {docsList.map((attachment) => {
                    const { id: docId, file, file_url, file_extension } = attachment;
                    const fileName =
                      file.original.length > DETAILS_FILE_NAME_LENGTH_LIMIT
                        ? hideDocNameMidPart({
                            docName: file.original,
                            limit: DETAILS_FILE_NAME_LENGTH_LIMIT,
                          })
                        : file.original;
                    return (
                      <DocumentAttachment
                        key={docId}
                        attachmentId={docId}
                        fileName={fileName}
                        file_extension={file_extension}
                        handleDelete={() => null}
                        hasDownload
                        originalFileName={file.original}
                        originalFileUrl={file_url.original}
                        blockDelete
                      />
                    );
                  })}
                </DocsGridW>
              </SectionW>
            )}

            {(imagesList.length > 0 || videosList.length > 0) && (
              <MediaSectionW>
                {imagesList.length > 0 && (
                  <MediaColumnW>
                    <SectionLabel>{t('equipments.attachments.filter.images')}</SectionLabel>
                    <AttachmentsW>
                      {imagesList.map((attachment) => (
                        <ListItemImageAttachment
                          key={attachment.id}
                          attachmentId={attachment.id}
                          fileName={attachment.file.small}
                          fileUrl={attachment.file_url.small}
                          wrapperSx={attachStyles}
                          onOpenGallery={handleOpenImageGallery}
                        />
                      ))}
                    </AttachmentsW>
                  </MediaColumnW>
                )}
                {videosList.length > 0 && (
                  <MediaColumnW>
                    <SectionLabel>{t('equipments.attachments.filter.videos')}</SectionLabel>
                    <AttachmentsW>
                      {videosList.map((attachment) => (
                        <VideoAttachment
                          key={attachment.id}
                          attachmentId={attachment.file_url.video_uid}
                          onOpenGallery={handleOpenVideoGallery}
                          wrapperSx={attachStyles}
                          blockDelete
                        />
                      ))}
                    </AttachmentsW>
                  </MediaColumnW>
                )}
              </MediaSectionW>
            )}
          </AttachmentsSectionW>
        )}
      </ContentW>

      {isOpenImageGallery && (
        <ImageGallery
          open={isOpenImageGallery}
          onClose={handleCloseImageGallery}
          imageList={imagesList}
        />
      )}

      {isOpenVideoGallery && (
        <VideoGallery
          open={isOpenVideoGallery}
          onClose={handleCloseVideoGallery}
          videoList={videosList}
        />
      )}
    </PageW>
  );
}
